import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import * as tus from 'tus-js-client';
import { useVideoCompression } from '@/hooks/useVideoCompression';

interface UploadItem {
  id: string;
  fileName: string;
  progress: number;
  status: 'compressing' | 'uploading' | 'complete' | 'error';
  clubId: string;
  folderId: string | null;
}

interface UploadContextType {
  uploads: UploadItem[];
  uploadFile: (file: File, clubId: string, folderId: string | null, userId: string, onComplete?: () => void) => Promise<void>;
  isUploading: boolean;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const { compressVideo, isCompressing, compressionProgress } = useVideoCompression();
  const completedIdsRef = useRef<Set<string>>(new Set());

  const updateUpload = useCallback((id: string, updates: Partial<UploadItem>) => {
    setUploads(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
  }, []);

  const removeUpload = useCallback((id: string) => {
    setTimeout(() => {
      setUploads(prev => prev.filter(u => u.id !== id));
    }, 3000); // Remove after 3 seconds
  }, []);

  const uploadFile = useCallback(async (
    file: File, 
    clubId: string, 
    folderId: string | null, 
    userId: string,
    onComplete?: () => void
  ) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const fileName = file.name.toLowerCase();
    
    // Validate file type
    const allowedDocExtensions = ['.ppt', '.pptx', '.xls', '.xlsx', '.doc', '.docx', '.pdf'];
    const videoExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v'];
    const allAllowedExtensions = [...allowedDocExtensions, ...videoExtensions];
    
    const hasValidExtension = allAllowedExtensions.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      toast.error('Only documents (PPT, Excel, Word, PDF) and videos (MP4, MOV, WebM) are allowed');
      return;
    }

    // Check file size - max 500MB
    const MAX_FILE_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(0)}MB). Maximum size is 500MB.`);
      return;
    }
    
    const isVideo = videoExtensions.some(ext => fileName.endsWith(ext));

    // Add to uploads list
    setUploads(prev => [...prev, {
      id: uploadId,
      fileName: file.name,
      progress: 0,
      status: isVideo && file.size > 20 * 1024 * 1024 ? 'compressing' : 'uploading',
      clubId,
      folderId
    }]);

    try {
      let fileToUpload = file;

      // Compress video if needed
      if (isVideo && file.size > 20 * 1024 * 1024) {
        toast.info(`Compressing ${file.name}...`, { duration: 2000 });
        fileToUpload = await compressVideo(file);
        updateUpload(uploadId, { status: 'uploading', progress: 0 });
        toast.success(`Compressed to ${(fileToUpload.size / 1024 / 1024).toFixed(1)}MB`, { duration: 2000 });
      }

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!token) {
        throw new Error('Authentication required');
      }

      const sanitizedName = fileToUpload.name
        .replace(/[^a-zA-Z0-9.-]/g, '_')
        .replace(/__+/g, '_');
      const filePath = `${userId}/${clubId}/${Date.now()}_${sanitizedName}`;

      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;

      // Upload with TUS
      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(fileToUpload, {
          endpoint: `https://${projectId}.supabase.co/storage/v1/upload/resumable`,
          retryDelays: [0, 1000, 3000, 5000],
          headers: {
            authorization: `Bearer ${token}`,
            apikey: supabaseKey,
          },
          uploadDataDuringCreation: true,
          removeFingerprintOnSuccess: true,
          chunkSize: 6 * 1024 * 1024,
          metadata: {
            bucketName: 'club-documents',
            objectName: filePath,
            contentType: fileToUpload.type,
            cacheControl: '3600',
          },
          onError: (error) => {
            console.error('TUS upload error:', error);
            reject(error);
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const percent = Math.round((bytesUploaded / bytesTotal) * 100);
            updateUpload(uploadId, { progress: percent });
          },
          onSuccess: () => {
            resolve();
          },
        });
        upload.start();
      });

      // Save to database
      const { error: insertError } = await supabase
        .from('club_files')
        .insert({
          club_id: clubId,
          folder_id: folderId,
          name: file.name, // Original name
          file_path: filePath,
          file_type: fileToUpload.type,
          file_size: fileToUpload.size,
          uploaded_by: userId,
        });

      if (insertError) throw insertError;

      updateUpload(uploadId, { status: 'complete', progress: 100 });
      toast.success(`${file.name} uploaded!`);
      onComplete?.();
      removeUpload(uploadId);

    } catch (error) {
      console.error('Upload error:', error);
      updateUpload(uploadId, { status: 'error' });
      toast.error(`Failed to upload ${file.name}`);
      removeUpload(uploadId);
    }
  }, [compressVideo, updateUpload, removeUpload]);

  const isUploading = uploads.some(u => u.status === 'uploading' || u.status === 'compressing');

  return (
    <UploadContext.Provider value={{ uploads, uploadFile, isUploading }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUpload() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within UploadProvider');
  }
  return context;
}
